import classNames from 'classnames';
import { type CSSProperties, useEffect, useRef, useState } from 'react';
import * as styles from './AnimatedTextScroll.css';

export function AnimatedTextScroll({
  words,
  currIdx,
  style,
}: {
  words: string[];
  currIdx: number;
  style: CSSProperties | undefined;
}) {
  const wordsCount = words?.length || 1;
  const prevIdxRaw = (currIdx - 1) % wordsCount;
  const prevIdx = prevIdxRaw >= 0 ? prevIdxRaw : wordsCount + prevIdxRaw;

  const scrollWrapperRef = useRef<HTMLSpanElement>(null);
  const [minHeight, setMinHeight] = useState<number>();
  const [minWidth, setMinWidth] = useState<number>();
  const [widths, setwidths] = useState<number[]>();
  const [isInOwnBlock, setIsInOwnBlock] = useState(false);

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      let childWidth = 0;

      if (!widths) setwidths(new Array(wordsCount).fill(0));
      entries.forEach((entry, idx) => {
        setwidths((prev) => {
          const newWidths = [...(prev || [])];
          newWidths[idx] = entry.contentRect?.width || 0;
          return newWidths;
        });
      });

      for (const entry of entries) {
        if (entry.contentRect?.width > childWidth) {
          childWidth = entry.contentRect?.width;
        }
      }
      setMinWidth(childWidth);
    });

    if (scrollWrapperRef.current) {
      Array.from(scrollWrapperRef.current.children).forEach((el) => {
        observer.observe(el);
      });

      const computedStyle = getComputedStyle(scrollWrapperRef.current) || {};
      const fontSize = Number.parseFloat(computedStyle.fontSize || '0');
      if (computedStyle.lineHeight === 'normal') {
        setMinHeight(fontSize * 1.2);
      } else {
        setMinHeight(Number.parseFloat(computedStyle.lineHeight || '0') || fontSize * 1.2);
      }
    }

    return () => observer.disconnect();
  }, [words]);

  useEffect(() => {
    const ancestorBlock = scrollWrapperRef.current?.closest('[class^="dream-"]');
    if (ancestorBlock && ancestorBlock.textContent === words.join('')) {
      setIsInOwnBlock(true);
    }
  }, [words]);

  return (
    <span
      ref={scrollWrapperRef}
      className={styles.scrollWrapper}
      style={{
        minHeight: minHeight || 'auto',
        minWidth: widths?.[currIdx] || minWidth,
      }}
    >
      {words.map((item, index) => (
        <span
          key={`text-animated-${item}`}
          className={classNames(styles.animated, {
            [styles.animateShow]: currIdx === index,
            [styles.animateUp]: prevIdx === index,
          })}
          style={{
            ...style,
            minWidth: isInOwnBlock ? '100%' : undefined,
          }}
        >
          {item}
        </span>
      ))}
    </span>
  );
}
